import { Component, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ResourceCapacityApiService } from '../../../Services/resource-modules-api.service';
import { ResourceApiService } from '../../../Services/resource-api.service';
import {
    ResourceCapacityGranularity, ResourceCapacityReport,
    ResourceCapacityQuery, ResourceEmployeeCapacity
} from '../../../Models/resource-modules.models';
import { ResourceKnowledge } from '../../../Models/resource.models';
import { ResourceAccessService, ResourceTeamOption } from '../../../Services/resource-access.service';

@Component({
    selector: 'app-resource-capacity',
    templateUrl: './resource-capacity.component.html',
    styleUrls: ['./resource-capacity.component.css']
})
export class ResourceCapacityComponent implements OnInit {
    report: ResourceCapacityReport | null = null;
    accessLoading = true;
    isGlobalPlanner = false;
    availableTeams: ResourceTeamOption[] = [];
    selectedTeamId: number | null = null;
    accessError = '';
    onTeamChange(): void { this.loadReport(); }

    skills: ResourceKnowledge[] = [];
    /** Globale Skills und die des gewählten Teams (teamspezifische Skills anderer Teams ausgeblendet). */
    get visibleSkills(): ResourceKnowledge[] {
        return this.skills.filter(s => !s.ownerTeamId || s.ownerTeamId === this.selectedTeamId);
    }
    loading = false;
    error = '';
    selectedSkillIds: number[] = [];
    searchText = '';
    granularity: ResourceCapacityGranularity = 'Week';
    from = '';
    to = '';
    expandedUserId: string | null = null;
    fromDate: Date | null = null;
    toDate: Date | null = null;
    private reportSubscription?: Subscription;


    readonly granularities: { value: ResourceCapacityGranularity; label: string }[] = [
        { value: 'Day', label: 'Tag' }, { value: 'Week', label: 'Woche' },
        { value: 'Month', label: 'Monat' }, { value: 'Quarter', label: 'Quartal' },
        { value: 'Year', label: 'Jahr' }
    ];

    constructor(
        private capacityApi: ResourceCapacityApiService,
        private resourceApi: ResourceApiService,
        private accessApi: ResourceAccessService
    ) {
        // Beim ersten Öffnen die aktuelle Kalenderwoche anzeigen.
        this.setPeriod(new Date(), this.granularity, false);
    }

    ngOnInit(): void {
        this.accessApi.getMine().subscribe({
            next: access => {
                this.isGlobalPlanner = access.isGlobalPlanner;
                this.availableTeams = access.planningTeams;
                // Startauswahl einmalig nach gemeinsamer Regel (Hauptteam vor „Gesamtplanung“).
                this.selectedTeamId = ResourceAccessService.defaultTeamId(
                    access, this.availableTeams.map(t => t.teamId), access.isGlobalPlanner);
                this.accessLoading = false;
                if (!access.isGlobalPlanner && this.selectedTeamId === null) {
                    this.accessError = 'Du leitest aktuell kein aktives Team.';
                    return;
                }
                this.resourceApi.getKnowledge().subscribe({
                    next: skills => { this.skills = skills; },
                    error: () => { this.error = 'Skills konnten nicht geladen werden.'; }
                });
                this.loadReport();
            },
            error: () => { this.accessLoading = false; this.accessError = 'Berechtigungen konnten nicht geladen werden.'; }
        });
    }


    private getPeriodRange(
        date: Date,
        granularity: ResourceCapacityGranularity
    ): { from: Date; to: Date } {

        const year = date.getFullYear();
        const month = date.getMonth();
        const day = date.getDate();

        let from = new Date(year, month, day);
        let to = new Date(year, month, day);

        switch (granularity) {
            case 'Day':
                break;

            case 'Week': {
                // Montag bis Sonntag
                const weekday = (from.getDay() + 6) % 7;

                from.setDate(from.getDate() - weekday);
                to = new Date(from);
                to.setDate(to.getDate() + 6);
                break;
            }

            case 'Month':
                from = new Date(year, month, 1);
                to = new Date(year, month + 1, 0);
                break;

            case 'Quarter': {
                const firstMonth = Math.floor(month / 3) * 3;

                from = new Date(year, firstMonth, 1);
                to = new Date(year, firstMonth + 3, 0);
                break;
            }

            case 'Year':
                from = new Date(year, 0, 1);
                to = new Date(year, 11, 31);
                break;
        }

        return { from, to };
    }

    private setPeriod(
        date: Date,
        granularity: ResourceCapacityGranularity,
        reload = true
    ): void {

        const range = this.getPeriodRange(date, granularity);

        // Sichtbare Datumsfelder
        this.fromDate = new Date(range.from);
        this.toDate = new Date(range.to);

        // API-Datumswerte
        this.from = this.dateKey(range.from);
        this.to = this.dateKey(range.to);

        if (reload) {
            this.loadReport();
        }
    }

    onGranularityChange(): void {
        // Bei jeder Ansichtsänderung den aktuellen Zeitraum anzeigen.
        this.setPeriod(new Date(), this.granularity);
    }

    get employees(): ResourceEmployeeCapacity[] {
        const search = this.searchText.trim().toLowerCase();
        return (this.report?.employees ?? [])
            .filter(e => !search || e.name.toLowerCase().includes(search))
            .sort((a, b) => a.name.localeCompare(b.name, 'de'));
    }

    get overloadedEmployees(): number {
        return (this.report?.employees ?? []).filter(e => e.totals.utilizationPercent > 100).length;
    }

    loadReport(): void {
        if (this.accessLoading || this.accessError || (!this.isGlobalPlanner && this.selectedTeamId === null)) return;
        if (!this.from || !this.to || this.from > this.to) {
            this.error = 'Bitte einen gültigen Zeitraum auswählen.';
            return;
        }
        this.reportSubscription?.unsubscribe();
        this.loading = true;
        this.error = '';
        this.expandedUserId = null;
        const query: ResourceCapacityQuery = {
            from: this.from,
            to: this.to,
            granularity: this.granularity,
            skillIds: this.selectedSkillIds,
            includeDays: true,
            teamId: this.selectedTeamId ?? undefined
        };
        this.reportSubscription = this.capacityApi.getReport(query).subscribe({
            next: report => { this.report = report; this.loading = false; },
            error: err => {
                this.loading = false;
                this.report = null;
                this.error = typeof err.error === 'string' && err.error.trim()
                    ? err.error : 'Kapazitätsbericht konnte nicht geladen werden.';
            }
        });
    }

    toggleEmployee(userId: string): void {
        this.expandedUserId = this.expandedUserId === userId ? null : userId;
    }

    barWidth(percent: number): number {
        return Number.isFinite(percent) ? Math.max(0, Math.min(100, percent)) : 0;
    }

    utilizationClass(percent: number): string {
        if (percent > 100) return 'overloaded';
        if (percent >= 85) return 'high';
        return 'normal';
    }
    parseDate(value: string): Date | null {
        if (!value) return null;

        const [year, month, day] = value.split('-').map(Number);

        if (!year || !month || !day) return null;

        return new Date(year, month - 1, day);
    }

    onDateSelected(field: 'from' | 'to'): void {

        const selectedDate =
            field === 'from' ? this.fromDate : this.toDate;

        if (!selectedDate || isNaN(selectedDate.getTime())) {
            return;
        }

        // Der ausgewählte Tag bestimmt den gesamten Zeitraum
        // entsprechend der aktuellen Ansicht.
        this.setPeriod(selectedDate, this.granularity);
    }

    setToday(): void {
        // Zur Tagesansicht wechseln
        this.granularity = 'Day';

        // Aktuellen Tag als Von- und Bis-Datum setzen
        this.setPeriod(new Date(), 'Day');
    }

    navigatePeriod(direction: -1 | 1): void {
        // Aktuell angezeigten Zeitraum als Ausgangspunkt verwenden
        const reference = this.fromDate
            ? new Date(this.fromDate)
            : new Date();

        switch (this.granularity) {
            case 'Day':
                reference.setDate(reference.getDate() + direction);
                break;

            case 'Week':
                reference.setDate(reference.getDate() + direction * 7);
                break;

            case 'Month':
                reference.setDate(1);
                reference.setMonth(reference.getMonth() + direction);
                break;

            case 'Quarter':
                reference.setDate(1);
                reference.setMonth(reference.getMonth() + direction * 3);
                break;

            case 'Year':
                reference.setFullYear(reference.getFullYear() + direction);
                break;
        }

        // Datumsfelder aktualisieren und Bericht neu laden
        this.setPeriod(reference, this.granularity);
    }

    private dateKey(date: Date): string {
        return [date.getFullYear(), String(date.getMonth() + 1).padStart(2, '0'),
        String(date.getDate()).padStart(2, '0')].join('-');
    }
}
